import { Accordion, Alert, Badge, Button, Drawer, Group, Loader, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useTeamNotifications } from '@/hooks/useTeamNotifications';
import {
  DEFAULT_RULE_KEY,
  NotificationRule,
  NotificationRuleInput,
  RECIPIENT_MODE_LABELS,
} from '@/types/team-settings';
import { toError } from '@/utils/errors';
import { CopyAddressManager } from './CopyAddressManager';
import { NotificationRuleEditor } from './NotificationRuleEditor';

interface TeamNotificationSettingsProps {
  teamId: string | null;
  teamName: string;
  opened: boolean;
  onClose: () => void;
}

function describeRule(rule: NotificationRule): string {
  const parts = [RECIPIENT_MODE_LABELS[rule.baseMode]];
  if (rule.baseMode === 'selected') {
    const count = rule.selectedUserIds.length;
    const chosen = count === 0 ? 'nobody chosen' : `${count} chosen`;
    parts[0] = rule.includeCreator
      ? `${parts[0]} (${count === 0 ? 'creator only' : `${chosen} + creator`})`
      : `${parts[0]} (${chosen})`;
  }
  if (rule.copyAddress) {
    parts.push(`copy to ${rule.copyAddress}`);
  }
  return parts.join(', ');
}

/**
 * Who is emailed about each report notification, for one client team.
 *
 * A team has an optional default and can override it for any notification; one
 * with neither follows the platform's default. The notifications listed come
 * from the API, so a new one appears here without a change to this screen.
 * Failures are shown here as they happen and never close the drawer, so what was
 * typed is still there to correct.
 */
export function TeamNotificationSettings({
  teamId,
  teamName,
  opened,
  onClose,
}: TeamNotificationSettingsProps) {
  const {
    events,
    notifications: settings,
    members,
    isLoading,
    error,
    membersError,
    refetch,
    saveRule,
    removeRule,
    registerAddress,
    resendAddress,
    removeAddress,
  } = useTeamNotifications({ teamId, enabled: opened });

  const fail = (title: string, err: unknown) => {
    notifications.show({ title, message: toError(err).message, color: 'red' });
  };

  const save = async (ruleKey: string, input: NotificationRuleInput, what: string) => {
    try {
      await saveRule(ruleKey, input);
      notifications.show({ title: 'Saved', message: `${what} updated.`, color: 'green' });
    } catch (err) {
      fail('Save failed', err);
    }
  };

  const remove = async (ruleKey: string, what: string) => {
    try {
      await removeRule(ruleKey);
      notifications.show({ title: 'Reset', message: what, color: 'green' });
    } catch (err) {
      fail('Reset failed', err);
    }
  };

  const register = async (email: string): Promise<boolean> => {
    try {
      await registerAddress(email);
      notifications.show({
        title: 'Confirmation sent',
        message: `${email} has been sent a link to confirm it.`,
        color: 'green',
      });
      return true;
    } catch (err) {
      // The address is saved even when the email fails, so it is listed and
      // can be resent; say so rather than leaving the admin to wonder.
      fail('Confirmation not sent', err);
      return false;
    }
  };

  const resend = async (addressId: string) => {
    try {
      await resendAddress(addressId);
      notifications.show({
        title: 'Confirmation sent',
        message: 'A new link was sent.',
        color: 'green',
      });
    } catch (err) {
      fail('Resend failed', err);
    }
  };

  const dropAddress = async (addressId: string) => {
    try {
      await removeAddress(addressId);
    } catch (err) {
      fail('Remove failed', err);
    }
  };

  const globalMode = settings?.globalDefaultMode ?? 'team';
  const defaultRule = settings?.defaultRule ?? null;
  const overrides = new Map((settings?.overrides ?? []).map((r) => [r.eventType, r]));
  const addresses = settings?.addresses ?? [];

  // A notification without its own rule starts from the team default.
  const seedFor = defaultRule ?? {
    baseMode: globalMode,
    selectedUserIds: [],
    includeCreator: false,
    copyAddress: null,
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={`Notifications — ${teamName}`}
    >
      {isLoading && !settings ? (
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : error ? (
        <Alert color="red" title="Couldn’t load notification settings">
          <Stack gap="xs" align="flex-start">
            <Text size="sm">{error.message}</Text>
            <Button size="xs" variant="light" onClick={() => void refetch()}>
              Try again
            </Button>
          </Stack>
        </Alert>
      ) : settings ? (
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Choose who is emailed about each report notification. A change applies from the next
            email. Whatever is chosen here, each person&apos;s own Backoffice notification
            preferences are respected.
          </Text>

          <Stack gap="xs">
            <Text fw={600}>Copy address</Text>
            <CopyAddressManager
              addresses={addresses}
              onRegister={register}
              onResend={resend}
              onRemove={dropAddress}
            />
          </Stack>

          <Stack gap="xs">
            <Group gap="xs">
              <Text fw={600}>Team default</Text>
              {!defaultRule && (
                <Badge size="xs" variant="outline" color="gray">
                  not set
                </Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              {defaultRule
                ? 'Used for every notification below that has no rule of its own.'
                : `Not set — notifications currently follow the platform default: ${RECIPIENT_MODE_LABELS[globalMode]}.`}
            </Text>
            <NotificationRuleEditor
              idPrefix="default"
              rule={defaultRule}
              seed={seedFor}
              members={members}
              membersUnavailable={membersError !== null}
              addresses={addresses}
              saveLabel={defaultRule ? 'Save default' : 'Set team default'}
              removeLabel="Reset to platform default"
              onSave={(input) => save(DEFAULT_RULE_KEY, input, 'The team default')}
              onRemove={() =>
                remove(DEFAULT_RULE_KEY, 'The team now follows the platform default.')
              }
            />
          </Stack>

          <Stack gap="xs">
            <Text fw={600}>Per notification</Text>
            <Accordion variant="separated" multiple>
              {events.map((event) => {
                const override = overrides.get(event.type) ?? null;
                return (
                  <Accordion.Item key={event.type} value={event.type}>
                    <Accordion.Control data-testid={`event-${event.type}`}>
                      <Stack gap={2}>
                        <Group gap="xs">
                          <Text size="sm" fw={500}>
                            {event.label}
                          </Text>
                          {override && (
                            <Badge size="xs" variant="light">
                              custom
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {override
                            ? describeRule(override)
                            : `Uses the ${defaultRule ? 'team' : 'platform'} default`}
                        </Text>
                      </Stack>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="xs">
                        <Text size="xs" c="dimmed">
                          {event.description}
                        </Text>
                        <NotificationRuleEditor
                          idPrefix={event.type}
                          rule={override}
                          seed={seedFor}
                          members={members}
                          membersUnavailable={membersError !== null}
                          addresses={addresses}
                          saveLabel={override ? 'Save' : 'Use a custom rule'}
                          removeLabel={`Use the ${defaultRule ? 'team' : 'platform'} default`}
                          onSave={(input) => save(event.type, input, event.label)}
                          onRemove={() =>
                            remove(event.type, `${event.label} now follows the default.`)
                          }
                        />
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Stack>
        </Stack>
      ) : null}
    </Drawer>
  );
}
